/**
 * Session-scoped native-app install banner.
 * Soft-show on key surfaces; also after a failed native open.
 * Dismiss sticks for the browser tab session (survives refresh).
 */
import { useCallback, useEffect, useState } from 'react';

/** Bump suffix when re-prompting after product policy changes. */
const STORAGE_KEY = 'x9.installHeadBanner.dismissed.v2';

function wasDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissedThisSession(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // private mode / quota — still hide for this mount
  }
}

export function useAppInstallBanner(options?: { softShowOnMount?: boolean }) {
  const softShowOnMount = options?.softShowOnMount ?? false;
  const [visible, setVisible] = useState(false);

  const show = useCallback(() => {
    if (wasDismissedThisSession()) return;
    setVisible(true);
  }, []);

  const showAfterFailedHandoff = show;

  const dismiss = useCallback(() => {
    markDismissedThisSession();
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!softShowOnMount) return;
    show();
  }, [softShowOnMount, show]);

  return { visible, show, showAfterFailedHandoff, dismiss };
}
